import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useSnackbar } from "notistack";
import {
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  Badge,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  AttachmentOutlined,
  EditRounded,
  FileDownloadOutlined,
  StarOutlined,
} from "@mui/icons-material";

import { AssignmentService } from "services";
import Tools from "utils/Tools";
import assignmentStatus from "common/constants/assignmentStatus";

import AssignmentDialog from "./AssignmentDialog";

import {
  AddButton,
  AssignmentFiles,
  AssignmentFilters,
  Layout,
  NoData,
  PageTitle,
  Paginator,
  Spinner,
  WriterCommentModal,
} from "components";
import CanI from "components/CanI";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -3,
    top: 5,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
  },
}));

const { getAssignments } = AssignmentService;
const { getDate, getErrorMessage, toFixed2 } = Tools;

const limit = 15;
const smallFontSize = { fontSize: 13 };

const COLUMNS = [
  { id: "status", label: "Estatus" },
  { id: "deliveryDate", label: "Entrega" },
  { id: "title", label: "Título" },
  { id: "clientName", label: "Cliente" },
  { id: "projectName", label: "Proyecto" },
  { id: "writerName", label: "Redactor" },
  { id: "editorName", label: "Editor" },
  { id: "pricePer1000", label: "Precio x 1000 ($)" },
  { id: "amount", label: "Monto ($)" },
  { id: "totalWords", label: "Palabras" },
];

const Assignments = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [viewAssignmentFiles, setViewAssignmentFiles] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedWriter, setSelectedWriter] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    COLUMNS.map(({ id }) => id),
  );
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isLoading) {
      const queryString = Tools.objectToQueryString(filters);
      getAssignments(queryString)
        .then(({ data }) => {
          setAssignments(data);
          setIsLoading(false);
        })
        .catch((error) => {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
          setIsLoading(false);
        });
    }
  }, [isLoading, enqueueSnackbar, filters]);

  const handleClose = () => {
    if (selectedAssignment) setSelectedAssignment(null);
    if (showAddForm) setShowAddForm(false);
  };

  const handleSearch = (filters) => {
    setFilters(filters);
    setIsLoading(true);
  };

  const handleChangePage = (page) => {
    setFilters({
      ...filters,
      offset: page === 1 ? null : `${(page - 1) * limit}`,
    });
    setIsLoading(true);
  };

  const handleCloseWriterCommentModal = () => {
    setSelectedWriter(null);
  };

  const handleClickWriter = (user) => {
    setSelectedWriter(user);
  };

  const handleVisibleColumnsChange = ({ target: { value } }) => {
    const selected = typeof value === "string" ? value.split(",") : value;
    const columnIds = COLUMNS.map(({ id }) => id);

    let next;
    if (selected.includes("all")) {
      if (visibleColumns.includes("all")) {
        const allStillSelected = columnIds.every((id) => selected.includes(id));
        if (allStillSelected) {
          next = [];
        } else {
          next = selected.filter((id) => id !== "all");
        }
      } else {
        next = ["all", ...columnIds];
      }
    } else if (visibleColumns.includes("all")) {
      next = [];
    } else {
      const allSelected = columnIds.every((id) => selected.includes(id));
      next = allSelected ? ["all", ...columnIds] : selected;
    }

    const hasVisibleColumn = COLUMNS.some(({ id }) => next.includes(id));
    setVisibleColumns(hasVisibleColumn ? next : ["title"]);
  };

  const pages = isLoading
    ? 0
    : Math.floor((assignments.total + limit - 1) / limit);

  return (
    <Layout>
      <PageTitle
        title="Asignaciones"
        actionButtons={[
          <CanI make="TASK_ADD">
            <Link to="/tareas">
              <Button onClick={null}>Tareas</Button>
            </Link>
          </CanI>,
          <CanI make="ASSIGNMENT_ADD">
            <AddButton onClick={() => setShowAddForm(true)} />
          </CanI>,
        ]}
      />
      <AssignmentFilters onSearch={handleSearch} />
      {isLoading && <Spinner text="Cargando asignaciones" isOpen small />}
      {!isLoading && (
        <div>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography>Total: {assignments.total}</Typography>
            <FormControl size="small" sx={{ minWidth: 300 }}>
              <InputLabel>Columnas visibles</InputLabel>
              <Select
                multiple
                value={visibleColumns}
                onChange={handleVisibleColumnsChange}
                input={<OutlinedInput label="Columnas visibles" />}
                renderValue={(selected) => {
                  const columnIds = COLUMNS.map(({ id }) => id);
                  if (
                    selected.includes("all") ||
                    (selected.length &&
                      columnIds.every((id) => selected.includes(id)))
                  ) {
                    return "Todos";
                  }
                  return selected
                    .filter((id) => id !== "all")
                    .map(
                      (id) =>
                        COLUMNS.find(({ id: columnId }) => columnId === id)
                          .label,
                    )
                    .join(", ");
                }}
              >
                <MenuItem value="all">
                  <Checkbox checked={visibleColumns.includes("all")} />
                  <ListItemText primary="Todos" />
                </MenuItem>
                {COLUMNS.map(({ id, label }) => (
                  <MenuItem key={id} value={id}>
                    <Checkbox
                      checked={
                        visibleColumns.includes("all") ||
                        visibleColumns.includes(id)
                      }
                    />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Box}>
            <Table>
              <TableHead>
                <TableRow>
                  {visibleColumns.includes("status") && (
                    <TableCell>Estatus</TableCell>
                  )}
                  {visibleColumns.includes("deliveryDate") && (
                    <TableCell>Entrega</TableCell>
                  )}
                  {visibleColumns.includes("title") && (
                    <TableCell>Título</TableCell>
                  )}
                  {visibleColumns.includes("clientName") && (
                    <TableCell>Cliente</TableCell>
                  )}
                  {visibleColumns.includes("projectName") && (
                    <TableCell>Proyecto</TableCell>
                  )}
                  {visibleColumns.includes("writerName") && (
                    <TableCell>Redactor</TableCell>
                  )}
                  {visibleColumns.includes("editorName") && (
                    <TableCell>Editor</TableCell>
                  )}
                  {visibleColumns.includes("pricePer1000") && (
                    <TableCell
                      style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                    >
                      Precio x 1000 ($)
                    </TableCell>
                  )}
                  {visibleColumns.includes("amount") && (
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      Monto ($)
                    </TableCell>
                  )}
                  {visibleColumns.includes("totalWords") && (
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      Palabras
                    </TableCell>
                  )}
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.data.map((assignment) => {
                  const {
                    amount,
                    clientName,
                    deliveryDate,
                    docUrl,
                    editorName,
                    id,
                    isFeatured,
                    pricePer1000,
                    projectName,
                    status,
                    title,
                    totalWords,
                    writerName,
                  } = assignment;

                  return (
                    <TableRow key={id}>
                      {visibleColumns.includes("status") && (
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {assignmentStatus[status].label}
                        </TableCell>
                      )}
                      {visibleColumns.includes("deliveryDate") && (
                        <TableCell sx={smallFontSize}>
                          {getDate({ date: deliveryDate, format: "short" })}
                        </TableCell>
                      )}
                      {visibleColumns.includes("title") && (
                        <TableCell>
                          <Link target="_blank" to={`/asignaciones/${id}`}>
                            {title}
                          </Link>
                        </TableCell>
                      )}
                      {visibleColumns.includes("clientName") && (
                        <TableCell sx={smallFontSize}>{clientName}</TableCell>
                      )}
                      {visibleColumns.includes("projectName") && (
                        <TableCell sx={smallFontSize}>{projectName}</TableCell>
                      )}
                      {visibleColumns.includes("writerName") && (
                        <TableCell sx={smallFontSize}>
                          <Button
                            endIcon={
                              !!isFeatured && (
                                <StarOutlined
                                  htmlColor="orange"
                                  fontSize="small"
                                />
                              )
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              textTransform: "none",
                              whiteSpace: "nowrap",
                            }}
                            variant="text"
                            size="small"
                            onClick={() => handleClickWriter(assignment)}
                          >
                            {writerName}
                          </Button>
                        </TableCell>
                      )}
                      {visibleColumns.includes("editorName") && (
                        <TableCell sx={smallFontSize}>
                          {editorName || ""}
                        </TableCell>
                      )}
                      {visibleColumns.includes("pricePer1000") && (
                        <TableCell sx={smallFontSize} align="right">
                          {pricePer1000 === null
                            ? "Fijo"
                            : pricePer1000
                              ? toFixed2(pricePer1000)
                              : ""}
                        </TableCell>
                      )}
                      {visibleColumns.includes("amount") && (
                        <TableCell sx={smallFontSize} align="right">
                          {amount ? toFixed2(amount) : ""}
                        </TableCell>
                      )}
                      {visibleColumns.includes("totalWords") && (
                        <TableCell sx={smallFontSize} align="right">
                          {totalWords || ""}
                        </TableCell>
                      )}
                      <TableCell>
                        <div style={{ display: "flex" }}>
                          <CanI make="ASSIGNMENT_EDIT">
                            <IconButton
                              size="small"
                              onClick={() => setSelectedAssignment(assignment)}
                            >
                              <EditRounded />
                            </IconButton>
                          </CanI>
                          {docUrl && (
                            <>
                              <CanI make="ASSIGNMENT_DOWNLOAD">
                                <a href={docUrl}>
                                  <IconButton size="small">
                                    <FileDownloadOutlined />
                                  </IconButton>
                                </a>
                              </CanI>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setViewAssignmentFiles(assignment)
                                }
                                disabled={!assignment.totalFiles}
                              >
                                <StyledBadge
                                  badgeContent={assignment.totalFiles}
                                  color="info"
                                >
                                  <AttachmentOutlined />
                                </StyledBadge>
                              </IconButton>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
      {!assignments?.data?.length && !isLoading && (
        <NoData message="No hay asignaciones" />
      )}
      {!!assignments?.data?.length && (
        <Paginator pages={pages} onChange={handleChangePage} />
      )}
      {(showAddForm || selectedAssignment) && (
        <AssignmentDialog
          assignment={selectedAssignment}
          onCancel={handleClose}
          onSuccess={() => setIsLoading(true)}
        />
      )}
      {viewAssignmentFiles && (
        <AssignmentFiles
          assignment={viewAssignmentFiles}
          onClose={() => setViewAssignmentFiles(null)}
        />
      )}
      {selectedWriter && (
        <WriterCommentModal
          user={selectedWriter}
          onClose={handleCloseWriterCommentModal}
        />
      )}
    </Layout>
  );
};

export default Assignments;
